// src/components/common/Sidebar.js - UPDATED VERSION
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLogo } from '../../hooks/useLogo';

const Sidebar = ({ collapsed, mobileOpen, onNavigate }) => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { logo } = useLogo(user?.user_type, user?.org_id);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/login');
    }
  };

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  // Navigation items based on user role and permissions
  const getNavigationItems = () => {
    const commonItems = [
      { to: '/dashboard', icon: 'fas fa-tachometer-alt', text: 'Dashboard', permission: null },
    ];

    const coreAdminItems = [
      { to: '/admin/landing-page', icon: 'fas fa-home', text: 'Landing Page', permission: 'settings.system' },
      { to: '/admin/help-content', icon: 'fas fa-life-ring', text: 'Help & Support (App)', permission: 'settings.system' },
      { to: '/organizations', icon: 'fas fa-sitemap', text: 'Organizations', permission: 'organizations.manage' },
      { to: '/users', icon: 'fas fa-users-cog', text: 'All Users', permission: 'staff.view_all' },
      { to: '/admin/users', icon: 'fas fa-user-shield', text: 'Admin Users', permission: 'settings.users' },
      { to: '/premises', icon: 'fas fa-map-marked-alt', text: 'All Premises', permission: 'premises.manage' },
      { to: '/shifts', icon: 'fas fa-business-time', text: 'Shifts', permission: 'staff.view' },
      { to: '/leaves', icon: 'fas fa-calendar-alt', text: 'Leave Management', permission: 'leaves.view' },
      { to: '/admin/settings', icon: 'fas fa-cog', text: 'Settings', permission: 'settings.edit' },
      { to: '/payment-gateways', icon: 'fas fa-coins', text: 'Payment Gateways', permission: 'organizations.manage' },
      { to: '/license-pricing', icon: 'fas fa-tags', text: 'License Pricing', permission: 'organizations.manage' },
      { to: '/admin/payment-management', icon: 'fas fa-money-check-alt', text: 'Payment Management', permission: 'organizations.manage' },
      { to: '/admin/refunds', icon: 'fas fa-undo', text: 'Refund Management', permission: 'organizations.manage' },
      { to: '/admin/invoices', icon: 'fas fa-file-invoice-dollar', text: 'Invoice Generator', permission: 'organizations.manage' },
      { to: '/admin/audit-trail', icon: 'fas fa-list-check', text: 'Audit Trail', permission: null },
      { to: '/analytics', icon: 'fas fa-money-check-dollar', text: 'Payment Analytics', permission: 'reports.view' },
      { to: '/reports', icon: 'fas fa-chart-line', text: 'Attendance Analytics', permission: 'reports.view' },
    ];

    const adminItems = [
      { to: '/my-organization', icon: 'fas fa-building', text: 'My Organization', permission: null },
      { to: '/staff', icon: 'fas fa-users', text: 'Staff Management', permission: 'staff.view' },
      { to: '/admin/users', icon: 'fas fa-user-shield', text: 'Admin Users', permission: null },
      { to: '/attendance', icon: 'fas fa-clock', text: 'Attendance', permission: 'attendance.view' },
      { to: '/leaves', icon: 'fas fa-calendar-alt', text: 'Leave Management', permission: 'leaves.view' },
      { to: '/premises', icon: 'fas fa-map-marked-alt', text: 'Premises', permission: 'premises.manage' },
      { to: '/shifts', icon: 'fas fa-business-time', text: 'Shifts', permission: 'staff.view' },
      { to: '/reports', icon: 'fas fa-chart-bar', text: 'Attendance Analytics', permission: 'reports.view' },
      { to: '/analytics', icon: 'fas fa-money-check-dollar', text: 'Payment Analytics', permission: 'reports.view' },
      { to: '/admin/settings', icon: 'fas fa-cog', text: 'Settings', permission: null },
      { to: '/testimonials', icon: 'fas fa-star', text: 'Testimonials', permission: null },
      { to: '/subscriptions', icon: 'fas fa-credit-card', text: 'Subscription', permission: 'settings.view' },
      { to: '/admin/invoices', icon: 'fas fa-file-invoice-dollar', text: 'Invoices', permission: 'settings.view' },
    ];

    const appAdminItems = [
      { to: '/staff', icon: 'fas fa-users', text: 'My Staff', permission: 'staff.view' },
      { to: '/attendance', icon: 'fas fa-clock', text: 'Attendance', permission: 'attendance.view' },
      { to: '/leaves', icon: 'fas fa-calendar-alt', text: 'Leave Requests', permission: 'leaves.view' },
      { to: '/shifts', icon: 'fas fa-business-time', text: 'Shifts', permission: 'staff.view' },

      { to: '/reports', icon: 'fas fa-chart-bar', text: 'Reports', permission: 'reports.view' },
    ];

    const staffItems = [
      { to: '/attendance', icon: 'fas fa-clock', text: 'My Attendance', permission: 'attendance.mark' },
      { to: '/leaves', icon: 'fas fa-calendar-alt', text: 'My Leaves', permission: 'leaves.apply' },
    ];

    let roleItems = [];
    if (user?.user_type === 'core_admin') {
      roleItems = coreAdminItems;
    } else if (user?.user_type === 'client_admin') {
      roleItems = adminItems;
    } else if (user?.user_type === 'app_admin') {
      roleItems = appAdminItems;
    } else {
      roleItems = staffItems;
    }

    // Filter items based on permissions
    const filteredItems = roleItems.filter(item =>
      !item.permission || hasPermission(item.permission)
    );

    return [...commonItems, ...filteredItems];
  };

  const navigationItems = getNavigationItems();
  const isAdmin = user?.user_type === 'core_admin' || user?.user_type === 'client_admin';
  const isCoreAdmin = user?.user_type === 'core_admin';
  const isClientAdmin = user?.user_type === 'client_admin';

  // Get organization info for display
  const getOrganizationInfo = () => {
    if (isCoreAdmin) {
      return {
        name: 'eHAAZRI Core',
        type: 'System Administration'
      };
    } else {
      return {
        name: user?.org_name || 'Organization',
        type: user?.user_type === 'client_admin' ? 'Admin Access' :
          user?.user_type === 'app_admin' ? 'Premises Manager' : 'Staff Access'
      };
    }
  };

  const orgInfo = getOrganizationInfo();

  // Check license status for client admin
  const getLicenseStatus = () => {
    if (!isClientAdmin) return null;

    const status = user?.license_status;
    console.log('License status ', status);
    const daysUntilExpiry = user?.days_until_expiry;
    console.log('daysUntilExpiry ', daysUntilExpiry);

    if (status === 'expired') {
      return {
        show: true,
        type: 'error',
        icon: 'fas fa-exclamation-circle',
        text: 'License Expired',
        color: '#ef4444'
      };
    }

    if (status === 'grace_period') {
      return {
        show: true,
        type: 'warning',
        icon: 'fas fa-exclamation-triangle',
        text: 'Grace Period',
        color: '#f59e0b'
      };
    }

    if (daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
      return {
        show: true,
        type: 'warning',
        icon: 'fas fa-clock',
        text: `${daysUntilExpiry} days left`,
        color: '#f59e0b'
      };
    }

    return null;
  };

  const licenseStatus = getLicenseStatus();

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''} ${isAdmin ? 'admin' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {logo ? (
            <img src={logo} alt="eHAAZRI Logo" className="sidebar-logo-img" />
          ) : (
            <>
              <div className="sidebar-logo-icon">H</div>
              <span className="sidebar-logo-text">eHAAZRI</span>
            </>
          )}
        </div>
        {!collapsed && (
          <div className="org-info">
            <div className="org-name">{orgInfo.name}</div>
            <div className="org-type">{orgInfo.type}</div>
          </div>
        )}
      </div>

      <div className="user-info">
        <div className="user-avatar" onClick={handleProfileClick}>
          <i className={isCoreAdmin ? 'fas fa-crown' :
            user?.user_type === 'client_admin' ? 'fas fa-user-tie' :
              user?.user_type === 'app_admin' ? 'fas fa-user-cog' : 'fas fa-user'}></i>
        </div>
        <div className="user-details">
          <div className="user-name">{user?.username || 'User'}</div>
          <div className="user-role">
            {isCoreAdmin ? 'Core Admin' :
              user?.user_type === 'client_admin' ? 'Organization Admin' :
                user?.user_type === 'app_admin' ? 'Premises Admin' : 'Staff Member'}
          </div>
          {user?.email_verified_at && (
            <div className="verified-badge">
              <i className="fas fa-check-circle"></i>
              <span>Verified</span>
            </div>
          )}
        </div>

        {/* Profile dropdown menu */}
        {showProfileMenu && (
          <div className="profile-dropdown">
            <div className="profile-menu-item">
              <i className="fas fa-user"></i>
              <span>View Profile</span>
            </div>
            <div className="profile-menu-item">
              <i className="fas fa-cog"></i>
              <span>Settings</span>
            </div>
            <div className="profile-menu-item">
              <i className="fas fa-key"></i>
              <span>Change Password</span>
            </div>
            <div className="profile-menu-divider"></div>
            <div className="profile-menu-item logout-item" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </div>
          </div>
        )}
      </div>

      {/* License Status Alert for Client Admin */}
      {licenseStatus && licenseStatus.show && !collapsed && (
        <div className="license-alert" style={{ borderLeftColor: licenseStatus.color }}>
          <i className={licenseStatus.icon} style={{ color: licenseStatus.color }}></i>
          <div className="license-alert-content">
            <div className="license-alert-title">{licenseStatus.text}</div>
            <button
              className="renew-btn"
              onClick={() => navigate('/subscriptions/purchase')}
            >
              Renew Now
            </button>
          </div>
        </div>
      )}

      <nav className="nav-menu">
        {navigationItems.map((item) => (
          <div key={item.to} className="nav-item">
            <NavLink
              to={item.to}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={onNavigate}
            >
              <i className={`${item.icon} nav-icon`}></i>
              <span className="nav-text">{item.text}</span>
            </NavLink>
          </div>
        ))}

        <div className="nav-item logout-section">
          <button className="nav-link logout-btn" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt nav-icon"></i>
            <span className="nav-text">Logout</span>
          </button>
        </div>
      </nav>

      {/* Professional Gradient Styles */}
      <style jsx>{`
        .sidebar {
          position: fixed;
          left: 0;
          top: 0;
          width: 280px;
          height: 100%;
          min-height: 100%;
          max-height: 100%;
          background: linear-gradient(180deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%);
          color: #ffffff;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
        }

        .sidebar.admin {
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          box-shadow: 4px 0 24px rgba(20, 184, 166, 0.25);
        }

        .sidebar.collapsed {
          width: 70px;
        }

        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-280px);
          }
          
          .sidebar.mobile-open {
            transform: translateX(0);
          }
          
          .sidebar.collapsed {
            width: 280px;
            transform: translateX(-280px);
          }
          
          .sidebar.collapsed.mobile-open {
            transform: translateX(0);
          }
        }

        .sidebar-header {
          padding: 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.15);
          background: rgba(255,255,255,0.05);
        }

        .sidebar-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 1rem;
          min-height: 60px;
          background: white;
          border-radius: 12px;
          padding: 8px 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .sidebar-logo-img {
          max-height: 45px;
          max-width: 180px;
          object-fit: contain;
          transition: all 0.3s ease;
        }

        .sidebar.collapsed .sidebar-logo {
          padding: 8px;
        }

        .sidebar.collapsed .sidebar-logo-img {
          max-width: 45px;
          max-height: 45px;
        }

        .sidebar-logo-icon {
          width: 45px;
          height: 45px;
          background: linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 1.3rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }

        .sidebar-logo-icon:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        }

        .sidebar-logo-text {
          font-size: 1.4rem;
          font-weight: 700;
          transition: opacity 0.3s ease;
          background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .sidebar.collapsed .sidebar-logo-text {
          opacity: 0;
        }

        .org-info {
          background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%);
          padding: 1rem;
          border-radius: 12px;
          transition: all 0.3s ease;
          border: 1px solid rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
        }

        .org-info:hover {
          background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.12) 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .sidebar.collapsed .org-info {
          opacity: 0;
          display: none;
        }

        .org-name {
          font-weight: 700;
          font-size: 0.95rem;
          margin-bottom: 0.3rem;
          color: #ffffff;
        }

        .org-type {
          font-size: 0.8rem;
          opacity: 0.9;
          color: rgba(255,255,255,0.85);
        }

        @media (max-width: 768px) {
          .sidebar.collapsed .sidebar-logo-text,
          .sidebar.collapsed .org-info {
            opacity: 1;
            display: block;
          }
        }

        .user-info {
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.15);
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
          background: rgba(255,255,255,0.05);
        }

        .user-avatar {
          width: 55px;
          height: 55px;
          background: linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          flex-shrink: 0;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 2px solid rgba(255,255,255,0.2);
        }

        .user-avatar:hover {
          background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.25) 100%);
          transform: scale(1.08);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
        }

        .user-details {
          transition: opacity 0.3s ease;
          flex: 1;
        }

        .sidebar.collapsed .user-details {
          opacity: 0;
        }

        @media (max-width: 768px) {
          .sidebar.collapsed .user-details {
            opacity: 1;
          }
        }

        .user-name {
          font-weight: 700;
          margin-bottom: 0.25rem;
          color: #ffffff;
          font-size: 0.95rem;
        }

        .user-role {
          font-size: 0.85rem;
          opacity: 0.9;
          margin-bottom: 0.35rem;
          color: rgba(255,255,255,0.85);
        }

        .verified-badge {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.75rem;
          opacity: 0.9;
          background: rgba(74, 222, 128, 0.15);
          padding: 0.2rem 0.5rem;
          border-radius: 12px;
          width: fit-content;
        }

        .verified-badge i {
          color: #4ade80;
          font-size: 0.85rem;
        }

        .profile-dropdown {
          position: absolute;
          top: 100%;
          left: 1.5rem;
          right: 1.5rem;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          box-shadow: 0 12px 32px rgba(0,0,0,0.25);
          padding: 0.75rem;
          z-index: 1001;
          color: #1e293b;
          border: 1px solid rgba(255,255,255,0.5);
        }

        .profile-menu-item {
          display: flex;
          align-items: center;
          gap: 0.85rem;
          padding: 0.85rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .profile-menu-item:hover {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          transform: translateX(4px);
        }

        .profile-menu-item i {
          width: 18px;
          color: #64748b;
        }

        .profile-menu-item.logout-item:hover {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #ef4444;
        }

        .profile-menu-item.logout-item:hover i {
          color: #ef4444;
        }

        .profile-menu-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, #e2e8f0 50%, transparent 100%);
          margin: 0.5rem 0;
        }

        .nav-menu {
          flex: 1;
          padding: 1rem 0;
          display: flex;
          flex-direction: column;
        }

        .nav-item {
          margin-bottom: 0.35rem;
          padding: 0 0.75rem;
        }

        .nav-link, .quick-action-btn {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 1rem 1.25rem;
          color: #ffffff;
          text-decoration: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          font-size: 0.95rem;
          border-radius: 12px;
          font-weight: 500;
          position: relative;
        }

        .nav-link::before, .quick-action-btn::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 0;
          height: 70%;
          background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.2) 100%);
          border-radius: 0 8px 8px 0;
          transition: width 0.3s ease;
        }

        .nav-link:hover, .quick-action-btn:hover {
          background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%);
          color: #ffffff;
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .nav-link:hover::before {
          width: 4px;
        }

        .nav-link.active {
          background: linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          font-weight: 600;
        }

        .nav-link.active::before {
          width: 4px;
          background: linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.8) 100%);
        }

        .logout-section {
          margin-top: auto;
          padding: 1rem 0.75rem 1.25rem;
          border-top: 1px solid rgba(255,255,255,0.15);
        }

        .logout-btn {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%);
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .logout-btn:hover {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(220, 38, 38, 0.2) 100%) !important;
          border-color: rgba(239, 68, 68, 0.35);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
        }

        .nav-icon {
          font-size: 1.2rem;
          width: 20px;
          text-align: center;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }

        .nav-link:hover .nav-icon {
          transform: scale(1.1);
        }

        .nav-text {
          transition: opacity 0.3s ease;
        }

        .sidebar.collapsed .nav-text {
          opacity: 0;
        }

        @media (max-width: 768px) {
          .sidebar.collapsed .nav-text {
            opacity: 1;
          }
        }

        /* Custom scrollbar for sidebar */
        .sidebar::-webkit-scrollbar {
          width: 12px;
        }

        .sidebar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 10px;
          margin: 4px;
        }

        .sidebar::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.35) 100%);
          border-radius: 10px;
          border: 2px solid rgba(255,255,255,0.15);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }

        .sidebar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.45) 100%);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
        }

        .sidebar::-webkit-scrollbar-thumb:active {
          background: linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.55) 100%);
        }

         .license-alert {
          margin: 1rem;
          padding: 1rem;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
          border-left: 4px solid;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 1rem;
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .license-alert:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }

        .license-alert i {
          font-size: 1.4rem;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        }

        .license-alert-content {
          flex: 1;
        }

        .license-alert-title {
          font-size: 0.9rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.65rem;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
        }

        .renew-btn {
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border: none;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #1e293b;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .renew-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
          background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
        }

        .renew-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </aside>
  );
};

export default Sidebar;
